import { useDemoFetcherWithSwr, useDemoSender } from "@/api";
import { userAtom } from "@/atoms/userAtom";
import { useAtomValue } from "jotai";
import { useCallback } from "react";

/**
 * ツアー完了状態を管理するカスタムフック。
 * unified-api の user_config（DynamoDB）で状態を管理する。
 */
export const useOnboardingTourStatus = () => {
  const user = useAtomValue(userAtom);
  const userId = user?.userId;

  // ユーザーがログイン済みの場合のみDBから取得
  const shouldFetch = !!userId;

  // DBからツアー状態を取得
  const {
    data: userConfig,
    error,
    isLoading,
    mutate
  } = useDemoFetcherWithSwr({
    apiName: "user_config",
    serviceName: "userConfig",
    operationId: "getUserConfig",
    requestParameters: {
      userId: userId || ""
    },
    enabled: shouldFetch,
    swrConfiguration: {
      revalidateOnFocus: false,
      revalidateOnReconnect: false,
      shouldRetryOnError: false
    }
  });

  // 既存設定の更新用sender
  const { sender: updateUserConfig } = useDemoSender({
    apiName: "user_config",
    serviceName: "userConfig",
    operationId: "updateUserConfig"
  });

  // 設定未作成ユーザー向けの作成用sender
  const { sender: createUserConfig } = useDemoSender({
    apiName: "user_config",
    serviceName: "userConfig",
    operationId: "createUserConfig"
  });

  /**
   * ツアー完了状態を更新する。
   * 設定が既に存在すれば更新し、存在しなければ新規作成する。
   */
  const updateTourStatus = useCallback(
    async (completed: boolean): Promise<void> => {
      if (!userId) {
        return;
      }

      try {
        if (userConfig) {
          // 既存の設定を更新
          await updateUserConfig({
            userId,
            requestBody: { onboarding_tour_completed: completed }
          });
        } else {
          // 設定が存在しない場合は新規作成
          await createUserConfig({
            requestBody: {
              user_id: userId,
              onboarding_tour_completed: completed
            }
          });
        }

        // SWRのキャッシュを更新
        await mutate();
      } catch (error) {
        console.error("ツアー状態の更新に失敗:", error);
      }
    },
    [userId, userConfig, updateUserConfig, createUserConfig, mutate]
  );

  /**
   * ツアー状態をリセットする（動作確認用）。
   */
  const resetTourStatus = useCallback(async (): Promise<void> => {
    await updateTourStatus(false);
  }, [updateTourStatus]);

  return {
    tourCompleted: userConfig?.onboarding_tour_completed ?? false,
    isLoading: shouldFetch ? isLoading : false,
    error,
    updateTourStatus,
    resetTourStatus
  };
};
